// src/pages/Signup.jsx
import React from "react";
import { Link } from "react-router-dom";
import { SignUp } from "@clerk/clerk-react";
import { Check, Star, Image as ImageIcon, Film, Link as LinkIcon } from "lucide-react";
import loginAvatar from "../assets/login-avatar.png";

// Social-proof avatars
import avatar1 from "../assets/avatar-1.jpg";
import avatar2 from "../assets/avatar-2.jpg";
import avatar3 from "../assets/avatar-3.jpg";
import avatar4 from "../assets/avatar-4.jpg";

const PANEL = "#111124";
const ACCENT = "#F82E88";
const GREEN = "#22c55e";
const BORDER = "#2A2A40";

const AVATARS = [avatar1, avatar2, avatar3, avatar4];

const PAGE_GRADIENT =
  "radial-gradient(circle at top, #26266b 0, #050514 55%, #000 100%)";

const SIGNUP_BONUS = {
  images: 5,
  videos: 1,
  urls: 1,
};

function StarterBundleCard() {
  return (
    <div
      className="mt-6 rounded-3xl border p-5 sm:p-6"
      style={{
        borderColor: `${GREEN}55`,
        background:
          "linear-gradient(180deg, rgba(34,197,94,0.10) 0%, rgba(18,18,40,0.92) 100%)",
        boxShadow: "0 14px 34px rgba(0,0,0,0.30)",
      }}
    >
      <div className="flex flex-col gap-4">
        <div className="text-left">
          <div className="flex items-center gap-2">
            <span
              className="px-3 py-1 rounded-full text-[11px] font-extrabold tracking-wide uppercase"
              style={{
                background: GREEN,
                color: "#04110D",
                boxShadow: "0 10px 24px rgba(34,197,94,0.30)",
                letterSpacing: "0.06em",
              }}
            >
              Free on signup
            </span>
          </div>

          <div className="mt-2 text-white text-xl sm:text-2xl font-extrabold">
            Start instantly — no credit card needed
          </div>
          <div className="mt-1 text-gray-200/90 text-sm sm:text-base">
            Every new account gets a starter bundle so you can test avatar videos,
            subtitles, and product-page-to-ad creation right away.
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div
            className="rounded-2xl border px-4 py-4 min-h-[124px] flex flex-col"
            style={{
              background: "#0f0f21",
              borderColor: BORDER,
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
            }}
          >
            <div className="text-white text-4xl sm:text-[2.5rem] font-extrabold leading-none tracking-tight">
              {SIGNUP_BONUS.images}
            </div>

            <div className="mt-4">
              <ImageIcon className="w-6 h-6 text-gray-200" />
            </div>

            <div className="mt-3 text-left">
              <div className="text-gray-200 text-sm font-semibold leading-tight">
                AI Images
              </div>
            </div>
          </div>

          <div
            className="rounded-2xl border px-4 py-4 min-h-[124px] flex flex-col"
            style={{
              background:
                "linear-gradient(180deg, rgba(248,46,136,0.10) 0%, rgba(20,20,40,0.85) 100%)",
              borderColor: `${ACCENT}55`,
              boxShadow: `0 0 0 1px ${ACCENT}22 inset`,
            }}
          >
            <div className="text-white text-4xl sm:text-[2.5rem] font-extrabold leading-none tracking-tight">
              {SIGNUP_BONUS.videos}
            </div>

            <div className="mt-4">
              <Film className="w-6 h-6 text-gray-200" />
            </div>

            <div className="mt-3 text-left">
              <div className="text-gray-200 text-sm font-semibold leading-tight">
                AI Videos
              </div>
            </div>
          </div>

          <div
            className="rounded-2xl border px-4 py-4 min-h-[124px] flex flex-col"
            style={{
              background: "#0f0f21",
              borderColor: BORDER,
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
            }}
          >
            <div className="text-white text-4xl sm:text-[2.5rem] font-extrabold leading-none tracking-tight">
              {SIGNUP_BONUS.urls}
            </div>

            <div className="mt-4">
              <LinkIcon className="w-6 h-6 text-gray-200" />
            </div>

            <div className="mt-3 text-left">
              <div className="text-gray-200 text-sm font-semibold leading-tight">
                Product Pages
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Signup() {
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-10"
      style={{
        background: PAGE_GRADIENT,
      }}
    >
      {/* MAIN GRID */}
      <div className="w-full max-w-6xl mx-auto grid gap-10 lg:grid-cols-[0.9fr,1.1fr] items-center">
        {/* LEFT SIDE – Hero, Benefits, Starter Bundle, Avatar Quote */}
        <div className="text-slate-50 lg:max-w-[560px] mx-auto lg:mx-0">
          {/* Main hero copy */}
          <div className="mb-6 text-center lg:text-left">
            <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-semibold leading-tight">
              Create your
              <span className="text-pink-400"> PixelAds</span> workspace
            </h1>
            <p className="mt-3 text-sm sm:text-base text-slate-300 max-w-xl mx-auto lg:mx-0">
              Turn product pages into high-performing ad creatives in just a few
              clicks. Analyze a URL and let AI generate avatar marketing videos
              with subtitles, photoshoot images, and export-ready ads.
            </p>
          </div>

          {/* Benefits list */}
          <div className="space-y-2 text-sm text-slate-200 max-w-md mx-auto lg:mx-0">
            <div className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 text-pink-400" />
              <span>Start free, no credit card required.</span>
            </div>
            <div className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 text-pink-400" />
              <span>From a single product URL to full ad creative packs.</span>
            </div>
            <div className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 text-pink-400" />
              <span>
                Avatar videos, subtitles, photos, captions, and watermarks in one
                workspace.
              </span>
            </div>
          </div>

          <StarterBundleCard />

          {/* Avatar / Testimonial block – white card */}
          <div
            className="mt-6 flex items-center gap-4 rounded-2xl border border-white/10 bg-white p-5 sm:p-6 shadow-2xl w-full mx-auto lg:mx-0"
            style={{ boxShadow: "0 24px 60px rgba(0,0,0,0.75)" }}
          >
            <img
              src={loginAvatar}
              alt="PixelAds user"
              className="h-14 w-14 rounded-full object-cover border border-pink-500/70"
            />
            <div className="text-sm">
              <p className="text-slate-900 leading-snug">
                “We launched dozens of new ad creatives in a single week with
                PixelAds. It&apos;s our go-to tool for testing products fast.”
              </p>
              <p className="mt-2 text-xs font-medium text-slate-500">
                Alex, Performance Marketer
              </p>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE – Clerk SignUp card + meta card */}
        <div className="flex justify-center">
          <div className="w-full max-w-[640px]">
            <SignUp
              path="/signup"
              routing="path"
              signInUrl="/login"
              afterSignInUrl="/analyze"
              afterSignUpUrl="/analyze"
              appearance={{
                layout: {
                  socialButtonsPlacement: "top",
                  socialButtonsVariant: "blockButton",
                },
                elements: {
                  rootBox: "w-full flex justify-center",
                  card:
                    "w-full bg-white text-slate-900 rounded-2xl border border-slate-100 " +
                    "shadow-[0_24px_60px_rgba(15,23,42,0.65)] px-6 sm:px-8 pt-7 pb-6 " +
                    "flex flex-col items-stretch",
                  headerTitle:
                    "text-xl sm:text-2xl font-semibold text-center text-[#4318FF]",
                  headerSubtitle:
                    "mt-2 text-xs sm:text-sm text-slate-500 text-center",
                  socialButtonsBlockButton:
                    "w-full border border-slate-200 bg-slate-50 text-slate-900 hover:bg-slate-100 text-sm " +
                    "rounded-xl py-2.5 mb-3 transition-colors duration-150 font-medium " +
                    "flex items-center justify-center gap-2",
                  socialButtonsProviderIcon__google: "h-4 w-4",
                  socialButtonsBlockButton__lastUsed: "hidden",
                  badge: "hidden",
                  badge__lastUsed: "hidden",
                  dividerRow: "my-3 flex items-center",
                  dividerLine: "bg-slate-200",
                  dividerText:
                    "text-[11px] uppercase tracking-[0.18em] text-slate-400",
                  form: "space-y-3 w-full",
                  formField: "space-y-1 w-full",
                  formFieldLabel:
                    "text-xs font-medium text-slate-700 mb-1",
                  formFieldInput:
                    "w-full bg-white text-slate-900 border border-[#dbdde1] rounded-xl text-sm " +
                    "placeholder:text-slate-400 focus:border-[#4318FF] focus:ring-0 " +
                    "px-3 py-2.5",
                  formFieldAction:
                    "text-xs text-pink-500 hover:text-pink-600 font-medium block text-right mt-1",
                  formButtonPrimary:
                    "mt-4 w-full rounded-xl py-3 text-sm font-medium " +
                    "bg-gradient-to-r from-[#605DFF] to-[#A855F7] text-white " +
                    "transition-all duration-200 " +
                    "hover:brightness-105 hover:-translate-y-[1px] hover:shadow-glow",
                  footer: "hidden",
                },
                variables: {
                  colorPrimary: ACCENT,
                  colorText: "#111827",
                  colorTextSecondary: "#4B5563",
                  colorBackground: PANEL,
                  borderRadius: "1rem",
                  fontSize: "14px",
                },
              }}
            />

            <div className="mt-3 mx-auto max-w-[400px] rounded-2xl bg-white bg-clip-border shadow-[0_18px_40px_rgba(15,23,42,0.55)] px-6 sm:px-8 py-4">
              <p className="text-[11px] sm:text-xs text-slate-500 text-center">
                By registering you agree to our{" "}
                <Link
                  to="/terms"
                  className="font-medium text-pink-500 hover:text-pink-600"
                >
                  terms &amp; privacy
                </Link>
                . Already have an account?{" "}
                <Link
                  to="/login"
                  className="font-medium text-pink-500 hover:text-pink-600"
                >
                  Log in
                </Link>
                .
              </p>

              <div className="mt-4 flex flex-col items-center">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {AVATARS.map((src, i) => (
                      <img
                        key={i}
                        src={src}
                        alt="PixelAds user"
                        className="h-8 w-8 sm:h-9 sm:w-9 rounded-full border-2 border-white object-cover"
                      />
                    ))}
                  </div>
                  <div className="flex gap-1 text-yellow-400">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Star
                        key={i}
                        className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400"
                      />
                    ))}
                  </div>
                </div>
                <p className="mt-1 text-[11px] sm:text-xs font-medium text-slate-700 text-center">
                  Used by founders &amp; marketers launching new products daily.
                </p>
              </div>

              <p className="mt-3 text-center text-[11px] text-slate-500">
                No credit card required • Cancel anytime • Meta &amp; Google
                export-ready creatives
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

